/*
 * One position model for scroll sliders: drag, page dots and prev/next buttons all stop at the same
 * "stops" (the scroll positions the slider can really reach), so they can never disagree.
 *
 * - Stops: every card that can align to the start ("card"), or every page of visible cards ("page",
 *   set with data-slide-by="page"). The last stop is always the end of the scroll.
 * - Dots: one per stop; extra dots are hidden. Buttons: disabled on the first/last stop.
 *   With a single stop (nothing to scroll), dots, buttons and their footer are hidden.
 * - Mouse drag works from anywhere (images, links, cards), follows the pointer exactly and settles on a
 *   stop from the release speed. Movement is animated frame by frame with snapping switched off, so the
 *   browser's scroll-snap and Prestige's scrollend handler cannot interrupt it.
 * - Swiper, Flickity and the slideshow keep their own behaviour; only stray clicks after a swipe are blocked.
 * Touch devices keep native scrolling; dots and buttons follow the scroll position.
 */
(() => {
  if (window.__mpCarouselInit) return;
  window.__mpCarouselInit = true;

  const SLIDERS = 'scroll-carousel, slideshow-carousel, .swiper, .flickity-enabled, .scroll-area, .enhanced-product-track, [data-mp-drag]';
  const SCROLLERS = 'scroll-carousel, .scroll-area, .enhanced-product-track, [data-mp-drag]';
  const OWN_DRAG = '.swiper, .flickity-enabled, slideshow-carousel';
  const UNMANAGED = '.product-gallery__carousel';
  const IGNORE = 'input, select, textarea, [contenteditable], .no-drag';
  const DRAG_THRESHOLD = 6;

  const style = document.createElement('style');
  style.textContent = [
    '.mp-carousel-moving{scroll-snap-type:none!important;scroll-behavior:auto!important}',
    '.mp-carousel-dragging{cursor:grabbing}.mp-carousel-dragging *{pointer-events:none!important}',
    '[data-mp-idle],[data-mp-extra]{display:none!important}',
  ].join('');
  document.head.appendChild(style);

  /* ------------------------------------------------------------------------------------------------
   * Stops
   * ---------------------------------------------------------------------------------------------- */

  const cellsOf = (el) => {
    const cells = 'cells' in el ? el.cells : Array.from(el.children);
    return cells.filter((cell) => cell.offsetParent !== null);
  };

  const maxScroll = (el) => Math.max(0, el.scrollWidth - el.clientWidth);

  const computeStops = (el) => {
    const max = maxScroll(el);
    if (max <= 1 || el.clientWidth === 0) return [0];
    const box = el.getBoundingClientRect();
    const pad = parseFloat(getComputedStyle(el).scrollPaddingInlineStart) || 0;
    const cells = cellsOf(el);
    const lefts = cells.map((cell) => cell.getBoundingClientRect().left - box.left - el.clientLeft + el.scrollLeft - pad);

    let picked = lefts;
    if (el.dataset.slideBy === 'page' && cells.length) {
      const perPage = Math.max(1, cells.filter((cell, i) => lefts[i] - lefts[0] + cell.getBoundingClientRect().width <= el.clientWidth + 1).length);
      picked = lefts.filter((_, i) => i % perPage === 0);
    }

    const stops = [];
    picked.concat(max).forEach((left) => {
      const value = Math.round(Math.max(0, Math.min(max, left)));
      if (!stops.some((stop) => Math.abs(stop - value) <= 2)) stops.push(value);
    });
    return stops.sort((a, b) => a - b);
  };

  const nearestIndex = (stops, left) => stops.reduce((best, stop, i) => (Math.abs(stop - left) < Math.abs(stops[best] - left) ? i : best), 0);

  /* ------------------------------------------------------------------------------------------------
   * Movement
   * ---------------------------------------------------------------------------------------------- */

  const animations = new WeakMap();

  const stopAnimation = (el) => {
    const anim = animations.get(el);
    if (anim) cancelAnimationFrame(anim.frame);
    animations.delete(el);
  };

  const scrollToStop = (el, index, stops = computeStops(el)) => {
    const target = stops[Math.max(0, Math.min(stops.length - 1, index))];
    stopAnimation(el);
    const from = el.scrollLeft;
    const distance = target - from;
    const state = getState(el);
    if (state) state.targetIndex = stops.indexOf(target);

    if (Math.abs(distance) < 1 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.classList.add('mp-carousel-moving');
      el.scrollLeft = target;
      requestAnimationFrame(() => el.classList.remove('mp-carousel-moving'));
      if (state) state.targetIndex = null;
      refresh(el);
      return;
    }

    const duration = Math.min(650, Math.max(280, 220 + Math.abs(distance) * 0.35));
    const start = performance.now();
    el.classList.add('mp-carousel-moving');
    refresh(el);

    const step = (now) => {
      // The frame timestamp can be earlier than `start`; a negative t would ease backwards.
      const t = Math.min(1, Math.max(0, (now - start) / duration));
      const eased = 1 - Math.pow(1 - t, 3);
      el.scrollLeft = from + distance * eased;
      if (t < 1) {
        animations.get(el).frame = requestAnimationFrame(step);
        return;
      }
      el.scrollLeft = target;
      animations.delete(el);
      requestAnimationFrame(() => {
        if (!animations.has(el)) el.classList.remove('mp-carousel-moving');
      });
      if (state) state.targetIndex = null;
      refresh(el);
    };
    animations.set(el, { frame: requestAnimationFrame(step) });
  };

  /* ------------------------------------------------------------------------------------------------
   * Dots and buttons
   * ---------------------------------------------------------------------------------------------- */

  const states = new WeakMap();

  const controlsOf = (el) => {
    if (!el.id) return { navs: [], prevs: [], nexts: [] };
    const id = CSS.escape(el.id);
    return {
      navs: Array.from(document.querySelectorAll(`carousel-navigation.page-dots[aria-controls="${id}"]`)),
      prevs: Array.from(document.querySelectorAll(`carousel-prev-button[aria-controls="${id}"]`)),
      nexts: Array.from(document.querySelectorAll(`carousel-next-button[aria-controls="${id}"]`)),
    };
  };

  const getState = (el) => states.get(el);

  const setIdle = (node, idle) => {
    if (node) node.toggleAttribute('data-mp-idle', idle);
  };

  const render = (el) => {
    const state = getState(el);
    if (!state) return;
    state.frame = null;
    if (el.clientWidth === 0) return;

    const stops = computeStops(el);
    const atEnd = el.scrollLeft >= maxScroll(el) - 1;
    let active = state.targetIndex ?? (atEnd ? stops.length - 1 : nearestIndex(stops, el.scrollLeft));
    active = Math.max(0, Math.min(stops.length - 1, active));
    const idle = stops.length <= 1;
    const { navs, prevs, nexts } = controlsOf(el);

    navs.forEach((nav) => {
      setIdle(nav, idle);
      Array.from(nav.querySelectorAll('button')).forEach((button, i) => {
        button.toggleAttribute('data-mp-extra', i >= stops.length);
        button.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    });
    prevs.forEach((prev) => {
      const button = prev.querySelector('button');
      if (button) button.disabled = active <= 0;
    });
    nexts.forEach((next) => {
      const button = next.querySelector('button');
      if (button) button.disabled = active >= stops.length - 1;
    });
    prevs.concat(nexts).forEach((control) => setIdle(control.closest('.mp-carousel-arrows') || control, idle));
    navs.concat(prevs, nexts).forEach((control) => {
      setIdle(control.closest('.mp-carousel-footer, .mp-blog-arrows, [data-mp-carousel-footer]'), idle);
    });
  };

  const refresh = (el) => {
    const state = getState(el);
    if (state && !state.frame) state.frame = requestAnimationFrame(() => render(el));
  };

  const manage = (el) => {
    if (states.has(el) || !el.matches('scroll-carousel') || el.matches(UNMANAGED)) return;
    states.set(el, { frame: null, targetIndex: null });
    el.addEventListener('scroll', () => refresh(el), { passive: true });
    // Prestige's own navigation updates the dots on this event; render again after it.
    el.addEventListener('carousel:change', () => refresh(el));
    if ('ResizeObserver' in window) new ResizeObserver(() => refresh(el)).observe(el);
    refresh(el);
  };

  const init = () => document.querySelectorAll('scroll-carousel').forEach(manage);

  // Prestige disables the buttons 100px before the edge; the stops decide instead.
  ['scroll:edge-nearing', 'scroll:edge-leaving'].forEach((type) => {
    document.addEventListener(type, (event) => {
      if (states.has(event.target)) event.stopImmediatePropagation();
    }, true);
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('carousel-prev-button, carousel-next-button, carousel-navigation.page-dots button');
    if (!control) return;
    const owner = control.closest('[aria-controls]');
    const el = owner && document.getElementById(owner.getAttribute('aria-controls'));
    if (!el || !states.has(el)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const stops = computeStops(el);
    const state = getState(el);
    const current = state.targetIndex ?? nearestIndex(stops, el.scrollLeft);
    if (control.matches('carousel-prev-button')) scrollToStop(el, current - 1, stops);
    else if (control.matches('carousel-next-button')) scrollToStop(el, current + 1, stops);
    else scrollToStop(el, Array.from(owner.querySelectorAll('button')).indexOf(control), stops);
  }, true);

  /* ------------------------------------------------------------------------------------------------
   * Mouse drag
   * ---------------------------------------------------------------------------------------------- */

  document.addEventListener('dragstart', (event) => {
    if (event.target instanceof Element && event.target.closest(SLIDERS)) event.preventDefault();
  });

  const suppressNextClick = () => {
    const suppress = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    window.addEventListener('click', suppress, { capture: true, once: true });
    window.setTimeout(() => window.removeEventListener('click', suppress, true), 0);
  };

  const findScroller = (target) => {
    if (target.closest(OWN_DRAG)) return null;
    let el = target.closest(SCROLLERS);
    while (el) {
      if (el.scrollWidth > el.clientWidth + 1 && getComputedStyle(el).overflowX !== 'visible') return el;
      el = el.parentElement && el.parentElement.closest(SCROLLERS);
    }
    return null;
  };

  let drag = null;

  const onPointerMove = (event) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const dx = event.clientX - drag.x;
    if (!drag.active) {
      if (Math.abs(dx) < DRAG_THRESHOLD || Math.abs(dx) < Math.abs(event.clientY - drag.y)) return;
      drag.active = true;
      stopAnimation(drag.el);
      drag.el.classList.add('mp-carousel-moving', 'mp-carousel-dragging');
      try { drag.el.setPointerCapture(event.pointerId); } catch (e) {}
      document.documentElement.style.userSelect = 'none';
    }
    event.preventDefault();
    drag.samples.push({ t: event.timeStamp, x: event.clientX });
    while (drag.samples.length > 2 && event.timeStamp - drag.samples[0].t > 100) drag.samples.shift();
    drag.pending = drag.left - dx;
    if (!drag.frame) {
      drag.frame = requestAnimationFrame(() => {
        if (!drag) return;
        drag.frame = null;
        drag.el.scrollLeft = drag.pending;
      });
    }
  };

  const onPointerUp = (event) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    document.removeEventListener('pointermove', onPointerMove, true);
    document.removeEventListener('pointerup', onPointerUp, true);
    document.removeEventListener('pointercancel', onPointerUp, true);
    const current = drag;
    drag = null;
    if (!current.active) return;

    const { el, samples } = current;
    if (current.frame) cancelAnimationFrame(current.frame);
    el.scrollLeft = current.pending;
    try { el.releasePointerCapture(event.pointerId); } catch (e) {}
    el.classList.remove('mp-carousel-dragging');
    document.documentElement.style.removeProperty('user-select');
    suppressNextClick();

    const first = samples[0];
    const last = samples[samples.length - 1];
    const elapsed = last && first ? last.t - first.t : 0;
    const velocity = elapsed > 0 ? -(last.x - first.x) / elapsed : 0;
    const stops = computeStops(el);
    const moved = el.scrollLeft - current.left;
    let index = nearestIndex(stops, el.scrollLeft + velocity * 220);
    const startIndex = nearestIndex(stops, current.left);
    if (index === startIndex && Math.abs(moved) > 30) index += Math.sign(moved);
    scrollToStop(el, index, stops);
  };

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !(event.target instanceof Element)) return;
    if (event.target.closest(IGNORE)) return;
    const el = findScroller(event.target);
    if (!el) return;
    if (el.matches('scroll-carousel')) manage(el);
    drag = { el, pointerId: event.pointerId, x: event.clientX, y: event.clientY, left: el.scrollLeft, pending: el.scrollLeft, samples: [{ t: event.timeStamp, x: event.clientX }], active: false, frame: null };
    document.addEventListener('pointermove', onPointerMove, true);
    document.addEventListener('pointerup', onPointerUp, true);
    document.addEventListener('pointercancel', onPointerUp, true);
  }, true);

  // Keep Prestige's own scroll-carousel mouse drag from running at the same time.
  document.addEventListener('mousedown', (event) => {
    if (!(event.target instanceof Element) || event.target.closest(IGNORE)) return;
    const el = event.target.closest('scroll-carousel[allow-drag]');
    if (el && !el.matches(UNMANAGED)) event.stopPropagation();
  }, true);

  // Sliders with their own drag still fire a click on the slide link after a swipe.
  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element) || !event.target.closest(OWN_DRAG)) return;
    const start = { x: event.clientX, y: event.clientY };
    document.addEventListener('pointerup', (up) => {
      if (Math.hypot(up.clientX - start.x, up.clientY - start.y) >= 10) suppressNextClick();
    }, { once: true, capture: true });
  }, true);

  Promise.all([customElements.whenDefined('scroll-carousel'), customElements.whenDefined('carousel-navigation')]).then(() => {
    init();
    document.addEventListener('shopify:section:load', init);
  });
})();
